import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { SHIPPING_NOTICE, CONTACT_INFO, SHIPPING_METHOD, SHIPPING_TIME, FREE_SHIPPING_THRESHOLD, siteImages } from '../data/mock';
import { Truck, Clock, Globe, Package, Phone, Mail } from 'lucide-react';

const ShippingPage = () => {
  useEffect(() => {
    document.title = 'Shipping Information | Keto Vybe';
  }, []);

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <CartDrawer />
      
      <main className="pt-20">
        {/* Banner */}
        <section className="relative h-64 lg:h-72 overflow-hidden">
          <img 
            src={siteImages.shopBanner}
            alt="Shipping"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <span className="text-vybe-400 font-semibold text-sm tracking-wider uppercase mb-3 block">Delivery</span>
              <h1 className="font-display text-4xl lg:text-5xl text-white mb-3">
                Shipping Info
              </h1>
              <p className="text-slate-400">Fast and reliable shipping across the USA</p>
            </div>
          </div>
        </section>

        {/* Free Shipping Banner */}
        <div className="bg-gradient-to-r from-vybe-600 to-vybe-500 py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center gap-3 text-white font-semibold">
              <Truck className="w-5 h-5" />
              {SHIPPING_NOTICE}
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Shipping Overview */}
          <div className="grid md:grid-cols-3 gap-4 mb-12">
            {[
              { icon: Truck, title: SHIPPING_METHOD, desc: 'Standard delivery', color: 'vybe' },
              { icon: Clock, title: SHIPPING_TIME, desc: 'Estimated delivery', color: 'cyan' },
              { icon: Globe, title: 'USA Only', desc: 'Shipping region', color: 'coral' },
            ].map((item, i) => (
              <div key={i} className="bg-slate-800/50 rounded-2xl p-6 text-center border border-slate-700/50">
                <div className={`w-14 h-14 bg-${item.color}-600/20 rounded-xl flex items-center justify-center mx-auto mb-4`}>
                  <item.icon className={`w-7 h-7 text-${item.color}-400`} />
                </div>
                <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                <p className="text-slate-400 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Shipping Details */}
          <div className="bg-slate-800/50 rounded-3xl p-8 lg:p-10 border border-slate-700/50 mb-10">
            <h2 className="font-display text-2xl text-white mb-8">Shipping Details</h2>
            
            <div className="space-y-8">
              <div>
                <h3 className="flex items-center gap-3 font-semibold text-white mb-3">
                  <div className="w-8 h-8 bg-vybe-600/20 rounded-lg flex items-center justify-center">
                    <Package className="w-4 h-4 text-vybe-400" />
                  </div>
                  Processing Time
                </h3>
                <p className="text-slate-400 pl-11">
                  Orders are typically processed within 1-2 business days. You will receive a shipping confirmation email with tracking information once your order ships.
                </p>
              </div>

              <div>
                <h3 className="flex items-center gap-3 font-semibold text-white mb-3">
                  <div className="w-8 h-8 bg-cyan-600/20 rounded-lg flex items-center justify-center">
                    <Clock className="w-4 h-4 text-cyan-400" />
                  </div>
                  Delivery Time
                </h3>
                <p className="text-slate-400 pl-11">
                  Standard shipping via {SHIPPING_METHOD} takes {SHIPPING_TIME} once your order has been shipped. Delivery times may vary based on your location.
                </p>
              </div>

              <div>
                <h3 className="flex items-center gap-3 font-semibold text-white mb-3">
                  <div className="w-8 h-8 bg-coral-600/20 rounded-lg flex items-center justify-center">
                    <Truck className="w-4 h-4 text-coral-400" />
                  </div>
                  Free Shipping
                </h3>
                <p className="text-slate-400 pl-11">
                  Enjoy FREE standard shipping on all orders of ${FREE_SHIPPING_THRESHOLD} or more within the continental United States.
                </p>
              </div>

              <div>
                <h3 className="flex items-center gap-3 font-semibold text-white mb-3">
                  <div className="w-8 h-8 bg-slate-700 rounded-lg flex items-center justify-center">
                    <Globe className="w-4 h-4 text-slate-300" />
                  </div>
                  Shipping Regions
                </h3>
                <p className="text-slate-400 pl-11">
                  We currently ship only within the United States. We do not ship to PO Boxes, APO/FPO addresses, or international locations at this time.
                </p>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-slate-900/50 border border-vybe-500/30 rounded-2xl p-6">
            <h3 className="font-semibold text-white mb-4">Questions About Your Order?</h3>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                className="flex items-center gap-2 text-vybe-400 hover:text-vybe-300 text-sm"
              >
                <Phone className="w-4 h-4" />
                {CONTACT_INFO.phone}
              </a>
              <a 
                href={`mailto:${CONTACT_INFO.email}`}
                className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300 text-sm"
              >
                <Mail className="w-4 h-4" />
                {CONTACT_INFO.email}
              </a>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ShippingPage;
